import _, { capitalize } from 'lodash';
import { useState } from 'react';
import useMain from '../../../../../hooks/useMain';
import { paginate } from '../../../../../utils/paginate';
import Pagination from '../../../../common/pagination';
import UsersSectionTable from './downlinesTable';
import FilterBar from './filterBar';
import FilterIndicators from './filterIndicators';

const UsersSection = ({ allUsers }) => {
	const pageSize = 12;
	const { initData } = useMain();
	const cards = Object.values(initData.cards);
	const titles = [
		{
			id: 0,
			name: 'userClass',
			label: 'User Class',
			options: [
				{
					id: '0_0',
					value: 'all',
					label: 'All Classes',
					fn: (users) => users,
				},
				{
					id: '0_1',
					value: 'planner',
					label: 'Planner',
					fn: (users) => users.filter((u) => u.userClass === 'planner'),
				},
				{
					id: '0_2',
					value: 'investor',
					label: 'Investor',
					fn: (users) => users.filter((u) => u.userClass === 'investor'),
				},
				{
					id: '0_3',
					value: 'none',
					label: 'No Class',
					fn: (users) => users.filter((u) => u.userClass === 'none'),
				},
			],
		},
		{
			id: 1,
			name: 'userType',
			label: 'User Type',
			options: [
				{
					id: '1_0',
					value: 'all',
					label: 'All Types',
					fn: (users) => users,
				},
				{
					id: '1_1',
					value: 'mmp',
					label: 'MMP',
					fn: (users) => users.filter((u) => u.active === 'Yes' && u.card !== 'white'),
				},
				{
					id: '1_2',
					value: 'mmi',
					label: 'MMI',
					fn: (users) => users.filter((u) => u.mmiIsActive),
				},
				{
					id: '1_3',
					value: 'none',
					label: 'Inactive',
					fn: (users) => users.filter((u) => u.active === 'No'),
				},
			],
		},
		{
			id: 2,
			name: 'userCard',
			label: 'User Card',
			options: [
				{ id: '2_0', value: 'all', label: 'All Cards', fn: (users) => users },
				...cards.map((card, i) => ({
					id: `2_${i + 1}`,
					value: card.name,
					label: capitalize(card.name),
					fn: (users) => users.filter((u) => u.card === card.name),
				})),
			],
		},
		{
			id: 3,
			name: 'userRanks',
			label: 'Ranks',
			options: [
				{ id: '3_0', value: 'all', label: 'All Ranks', fn: (users) => users },
				...initData.userRanks.map((rank, i) => ({
					id: `3_${i + 1}`,
					value: rank.name,
					label: capitalize(rank.title),
					fn: (users) => users.filter((u) => u.userRank === rank.name),
				})),
			],
		},
	];

	const [selectedOptions, setSelectedOptions] = useState(
		titles
			.reduce((acc, title) => [...acc, ...title.options], [])
			.filter(
				(optoin) => optoin.id.length === 3 && optoin.id.charAt(optoin.id.length - 1) === '0'
			)
	);
	const [usersCurrentPage, setUsersCurrentPage] = useState(1);
	const [sortColumn, setSortColumn] = useState({
		path: 'currentPackageValue',
		order: 'desc',
	});

	function filterUsersBySelectedOptions() {
		// Group selected options by title ID (e.g., 0, 1, 2, 3)
		const grouped = new Map();

		for (const option of selectedOptions) {
			const titleId = parseInt(option.id.split('_')[0]);
			if (!grouped.has(titleId)) grouped.set(titleId, []);
			grouped.get(titleId).push(option);
		}

		let filtered = allUsers;

		// Apply AND across groups
		for (const group of grouped.entries()) {
			const options = group[1];
			// OR within the group — union of users matching any option.fn
			const userSet = new Set();

			for (const opt of options) {
				const matches = opt.fn(allUsers); // apply fn to the full user list
				for (const user of matches) {
					userSet.add(user._id || user.id || JSON.stringify(user)); // add unique user IDs
				}
			}

			// Reduce the overall filtered list to only those in the current group
			filtered = filtered.filter((user) =>
				userSet.has(user._id || user.id || JSON.stringify(user))
			);
		}

		return filtered;
	}

	const filteredUsers = filterUsersBySelectedOptions();
	for (const i in filteredUsers) filteredUsers[i].no = Number(i) + 1;

	const sortedUsers = _.orderBy(filteredUsers, [sortColumn.path], [sortColumn.order]);
	const users = paginate(sortedUsers, usersCurrentPage, pageSize);

	const handleOptionSelect = (option) => {
		const selected = selectedOptions.find((o) => o.id === option.id);
		if (selected) {
			setSelectedOptions((prev) => prev.filter((o) => o.id !== option.id));
		} else {
			setSelectedOptions((prev) => {
				const newArray = [...prev, option];
				const titleId = parseInt(option.id.split('_')[0]);
				const optionId = parseInt(option.id.split('_')[1]);
				if (optionId === 0) {
					const filtered = newArray.filter(
						(o) => parseInt(o.id.split('_')[0]) !== titleId
					);
					return [...filtered, option];
				} else {
					const filtered = newArray.filter((o) => o.id !== `${titleId}_0`);
					return filtered;
				}
			});
		}
		setUsersCurrentPage(1);
	};

	const handleSort = (path) => {
		setSortColumn((prev) => {
			const sortColumn = { ...prev };
			if (!path) return sortColumn;
			if (prev.path === path) {
				sortColumn.order = sortColumn.order === 'asc' ? 'desc' : 'asc';
			} else {
				sortColumn.path = path;
				sortColumn.order = 'asc';
			}
			return sortColumn;
		});
		setUsersCurrentPage(1);
	};

	return (
		<div className='mb-4 card myCards'>
			<div className='card-body'>
				<div className='row userSectionRow'>
					<div className='col-xl-2 mb-3'>
						<FilterBar
							titles={titles}
							selectedOptions={selectedOptions}
							onOptionSelect={handleOptionSelect}
						/>
					</div>
					<div className='col-xl-10'>
						<FilterIndicators
							selectedOptions={selectedOptions}
							onOptionSelect={handleOptionSelect}
						/>
						<hr className='mt-1' />
						<UsersSectionTable
							users={users}
							sortColumn={sortColumn}
							onSort={handleSort}
						/>
						<div className='d-flex justify-content-between align-items-center mt-2'>
							<div>
								Users Count: <b>{filteredUsers.length}</b>
							</div>
							<Pagination
								customClass='myPagination'
								activeClass='myActive'
								totalCount={filteredUsers.length}
								pageSize={pageSize}
								currentPage={usersCurrentPage}
								onPageChange={setUsersCurrentPage}
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default UsersSection;
